import React, { useCallback, useEffect, useRef, useState } from "react";
import { toast } from "react-toastify";
import {
    listUsers,
    deleteUserById,
    unlockUser,
    lockUser,
    ROLE,
    ROLE_LABEL,
    ROLE_OPTIONS,
} from "../../services/UserAdminService";
import UserFormModal from "./UserFormModal";
import ConfirmDialog from "./ConfirmDialog";
import MaxDevicesCard from "./MaxDevicesCard";
import Pagination from "./Pagination";

const DEFAULT_LIMIT = 12;

function formatDate(iso) {
    if (!iso) return "—";
    const d = new Date(iso);
    if (Number.isNaN(d.getTime())) return "—";
    return d.toLocaleString("vi-VN", {
        year: "numeric",
        month: "2-digit",
        day: "2-digit",
        hour: "2-digit",
        minute: "2-digit",
    });
}

function RoleBadge({ role }) {
    const label = ROLE_LABEL[role] ?? `Role ${role}`;
    const styles = {
        0: "bg-gray-100 text-gray-700",
        1: "bg-red-50 text-red-700",
        2: "bg-amber-50 text-amber-700",
    };
    const cls = styles[role] || "bg-gray-100 text-gray-700";
    return (
        <span
            className={`inline-flex items-center px-2 py-0.5 rounded-md text-[11px] font-semibold ${cls}`}
        >
            {label}
        </span>
    );
}

function UserManagementPage() {
    const [rows, setRows] = useState([]);
    const [loading, setLoading] = useState(false);
    const [page, setPage] = useState(1);
    const [pageSize, setPageSize] = useState(DEFAULT_LIMIT);
    const [totalPages, setTotalPages] = useState(1);
    const [totalUsers, setTotalUsers] = useState(0);
    const [stats, setStats] = useState({
        totalActive: 0,
        totalLocked: 0,
        totalAll: 0,
    });
    const [search, setSearch] = useState("");
    const [role, setRole] = useState("");
    const [lockedFilter, setLockedFilter] = useState(""); // "", "true", "false"

    const [formOpen, setFormOpen] = useState(false);
    const [editingUser, setEditingUser] = useState(null);

    const [confirmDelete, setConfirmDelete] = useState(null); // user row
    const [deleting, setDeleting] = useState(false);

    const [confirmUnlock, setConfirmUnlock] = useState(null); // user row
    const [unlocking, setUnlocking] = useState(false);

    const [confirmLock, setConfirmLock] = useState(null); // user row
    const [locking, setLocking] = useState(false);

    // Debounce search
    const debounceRef = useRef(null);

    const load = useCallback(
        async ({ pageArg, qArg, roleArg, lockedArg, limitArg } = {}) => {
            setLoading(true);
            try {
                const res = await listUsers({
                    page: pageArg ?? page,
                    limit: limitArg ?? pageSize,
                    q: qArg ?? search,
                    role: roleArg ?? role,
                    isLocked: lockedArg ?? lockedFilter,
                });
                setRows(res?.data || []);
                setTotalPages(res?.totalPages || 1);
                setTotalUsers(res?.totalUsers || 0);
                setStats({
                    totalActive: res?.totalActive ?? 0,
                    totalLocked: res?.totalLocked ?? 0,
                    totalAll: res?.totalAll ?? 0,
                });
            } catch (err) {
                const msg =
                    err?.response?.data?.message ||
                    err.message ||
                    "Không tải được danh sách user";
                toast.error(msg);
            } finally {
                setLoading(false);
            }
        },
        // eslint-disable-next-line react-hooks/exhaustive-deps
        [page, pageSize, search, role, lockedFilter]
    );

    // Initial + khi page/pageSize/role/lockedFilter thay đổi
    useEffect(() => {
        load();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [page, pageSize, role, lockedFilter]);

    // Debounce search
    const handleSearchChange = (e) => {
        const v = e.target.value;
        setSearch(v);
        if (debounceRef.current) clearTimeout(debounceRef.current);
        debounceRef.current = setTimeout(() => {
            setPage(1);
            load({ pageArg: 1, qArg: v });
        }, 350);
    };

    const handleRoleChange = (e) => {
        const v = e.target.value;
        setRole(v);
        setPage(1);
    };

    const handleLockedFilterChange = (e) => {
        const v = e.target.value;
        setLockedFilter(v);
        setPage(1);
    };

    const openCreate = () => {
        setEditingUser(null);
        setFormOpen(true);
    };

    const openEdit = (user) => {
        setEditingUser(user);
        setFormOpen(true);
    };

    const handleDelete = async () => {
        if (!confirmDelete) return;
        setDeleting(true);
        try {
            await deleteUserById(confirmDelete._id);
            toast.success("Xóa user thành công");
            setConfirmDelete(null);
            // Nếu xóa item cuối của trang → lùi 1 trang
            if (rows.length === 1 && page > 1) {
                setPage((p) => p - 1);
            } else {
                load();
            }
        } catch (err) {
            const msg =
                err?.response?.data?.message || err.message || "Xóa user thất bại";
            toast.error(msg);
        } finally {
            setDeleting(false);
        }
    };

    const handleUnlock = async () => {
        if (!confirmUnlock) return;
        setUnlocking(true);
        try {
            const res = await unlockUser(confirmUnlock._id);
            toast.success(res?.message || "Đã mở khoá tài khoản");
            setConfirmUnlock(null);
            load();
        } catch (err) {
            const msg =
                err?.response?.data?.message ||
                err.message ||
                "Mở khoá thất bại";
            toast.error(msg);
        } finally {
            setUnlocking(false);
        }
    };

    const handleLock = async () => {
        if (!confirmLock) return;
        setLocking(true);
        try {
            const res = await lockUser(confirmLock._id);
            toast.success(res?.message || "Đã khoá tài khoản");
            setConfirmLock(null);
            load();
        } catch (err) {
            const msg =
                err?.response?.data?.message ||
                err.message ||
                "Khoá thất bại";
            toast.error(msg);
        } finally {
            setLocking(false);
        }
    };

    return (
        <div className="flex-1 min-h-0 overflow-y-auto">
            <div className="max-w-6xl mx-auto p-6">
                {/* Header */}
                <div className="flex flex-wrap items-end justify-between gap-3 mb-5">
                    <div>
                        <h1 className="text-xl font-semibold text-gray-900">
                            Quản lý User
                        </h1>
                        <div className="mt-1 flex items-center flex-wrap gap-2 text-sm">
                            <span className="text-gray-500">
                                Tổng cộng{" "}
                                <span className="font-semibold text-gray-700">
                                    {stats.totalAll || totalUsers}
                                </span>{" "}
                                user
                            </span>
                            <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-md text-[11px] font-semibold bg-emerald-50 text-emerald-700">
                                <span className="w-1.5 h-1.5 rounded-full bg-emerald-500" />
                                Hoạt động: {stats.totalActive}
                            </span>
                            <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-md text-[11px] font-semibold bg-red-50 text-red-700">
                                <span className="w-1.5 h-1.5 rounded-full bg-red-500" />
                                Đã khóa: {stats.totalLocked}
                            </span>
                        </div>
                    </div>
                    <button
                        type="button"
                        onClick={openCreate}
                        className="inline-flex items-center gap-2 px-4 py-2 bg-red-600 text-white text-sm font-medium rounded-lg hover:bg-red-700 transition-colors"
                    >
                        <svg
                            className="w-4 h-4"
                            fill="none"
                            stroke="currentColor"
                            viewBox="0 0 24 24"
                        >
                            <path
                                strokeLinecap="round"
                                strokeLinejoin="round"
                                strokeWidth={2}
                                d="M12 4v16m8-8H4"
                            />
                        </svg>
                        Tạo user mới
                    </button>
                </div>

                {/* System config */}
                <MaxDevicesCard />

                {/* Filters */}
                <div className="flex flex-wrap items-center gap-3 mb-4 p-3 bg-white border border-gray-200 rounded-xl">
                    <div className="relative flex-1 min-w-[220px]">
                        <svg
                            className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 pointer-events-none"
                            fill="none"
                            stroke="currentColor"
                            viewBox="0 0 24 24"
                        >
                            <path
                                strokeLinecap="round"
                                strokeLinejoin="round"
                                strokeWidth={2}
                                d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z"
                            />
                        </svg>
                        <input
                            type="text"
                            value={search}
                            onChange={handleSearchChange}
                            placeholder="Tìm theo email, username, trường…"
                            className="w-full pl-9 pr-3 py-2 text-sm border border-gray-300 rounded-lg outline-none focus:border-red-600 transition-colors"
                        />
                    </div>

                    <select
                        value={role}
                        onChange={handleRoleChange}
                        className="px-3 py-2 text-sm border border-gray-300 rounded-lg outline-none focus:border-red-600 bg-white"
                    >
                        <option value="">Tất cả vai trò</option>
                        {ROLE_OPTIONS.map((opt) => (
                            <option key={opt.value} value={opt.value}>
                                {opt.label}
                            </option>
                        ))}
                    </select>

                    <select
                        value={lockedFilter}
                        onChange={handleLockedFilterChange}
                        className="px-3 py-2 text-sm border border-gray-300 rounded-lg outline-none focus:border-red-600 bg-white"
                    >
                        <option value="">Tất cả trạng thái</option>
                        <option value="false">Đang hoạt động</option>
                        <option value="true">Đã khóa</option>
                    </select>

                    <button
                        type="button"
                        onClick={() => {
                            setSearch("");
                            setRole("");
                            setLockedFilter("");
                            setPage(1);
                            load({
                                pageArg: 1,
                                qArg: "",
                                roleArg: "",
                                lockedArg: "",
                            });
                        }}
                        className="px-3 py-2 text-sm text-gray-600 hover:bg-gray-100 rounded-lg"
                    >
                        Reset
                    </button>
                </div>

                {/* Table */}
                <div className="bg-white border border-gray-200 rounded-xl overflow-hidden">
                    <div className="overflow-x-auto">
                        <table className="w-full text-sm">
                            <thead className="bg-gray-50 border-b border-gray-200">
                                <tr className="text-left">
                                    <th className="px-4 py-3 font-semibold text-gray-600 text-xs uppercase tracking-wider">
                                        User
                                    </th>
                                    <th className="px-4 py-3 font-semibold text-gray-600 text-xs uppercase tracking-wider">
                                        Role
                                    </th>
                                    <th className="px-4 py-3 font-semibold text-gray-600 text-xs uppercase tracking-wider">
                                        Trạng thái
                                    </th>
                                    <th className="px-4 py-3 font-semibold text-gray-600 text-xs uppercase tracking-wider">
                                        Ngày tạo
                                    </th>
                                    <th className="px-4 py-3 font-semibold text-gray-600 text-xs uppercase tracking-wider text-right">
                                        Thao tác
                                    </th>
                                </tr>
                            </thead>
                            <tbody className="divide-y divide-gray-100">
                                {loading && rows.length === 0 && (
                                    <tr>
                                        <td
                                            colSpan={5}
                                            className="px-4 py-12 text-center text-gray-400 text-sm"
                                        >
                                            <div className="inline-flex items-center gap-2">
                                                <div className="w-4 h-4 border-2 border-gray-300 border-t-red-600 rounded-full animate-spin" />
                                                Đang tải…
                                            </div>
                                        </td>
                                    </tr>
                                )}

                                {!loading && rows.length === 0 && (
                                    <tr>
                                        <td
                                            colSpan={5}
                                            className="px-4 py-12 text-center text-gray-400 text-sm"
                                        >
                                            Không có user phù hợp.
                                        </td>
                                    </tr>
                                )}

                                {rows.map((u) => (
                                    <tr key={u._id} className="hover:bg-gray-50">
                                        <td className="px-4 py-3">
                                            <div className="flex items-center gap-3 min-w-0">
                                                <div className="w-9 h-9 rounded-full bg-gradient-to-br from-red-600 to-red-700 flex items-center justify-center text-white text-sm font-semibold flex-shrink-0">
                                                    {(u.username || u.email || "U")
                                                        .charAt(0)
                                                        .toUpperCase()}
                                                </div>
                                                <div className="min-w-0">
                                                    <div className="font-medium text-gray-900 truncate">
                                                        {u.username || "—"}
                                                    </div>
                                                    <div className="text-xs text-gray-500 truncate">
                                                        {u.email}
                                                    </div>
                                                </div>
                                            </div>
                                        </td>
                                        <td className="px-4 py-3">
                                            <RoleBadge role={u.role} />
                                        </td>
                                        <td className="px-4 py-3">
                                            {u.isLocked ? (
                                                <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-md text-[11px] font-semibold bg-red-50 text-red-700">
                                                    <svg
                                                        className="w-3 h-3"
                                                        fill="none"
                                                        stroke="currentColor"
                                                        viewBox="0 0 24 24"
                                                    >
                                                        <path
                                                            strokeLinecap="round"
                                                            strokeLinejoin="round"
                                                            strokeWidth={2}
                                                            d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z"
                                                        />
                                                    </svg>
                                                    Đã khóa
                                                </span>
                                            ) : (
                                                <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-md text-[11px] font-semibold bg-emerald-50 text-emerald-700">
                                                    <svg
                                                        className="w-3 h-3"
                                                        fill="none"
                                                        stroke="currentColor"
                                                        viewBox="0 0 24 24"
                                                    >
                                                        <path
                                                            strokeLinecap="round"
                                                            strokeLinejoin="round"
                                                            strokeWidth={2}
                                                            d="M8 11V7a4 4 0 118 0m-4 8v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2z"
                                                        />
                                                    </svg>
                                                    Hoạt động
                                                </span>
                                            )}
                                        </td>
                                        <td className="px-4 py-3 text-gray-600 whitespace-nowrap">
                                            {formatDate(u.createdAt)}
                                        </td>
                                        <td className="px-4 py-3">
                                            <div className="flex items-center justify-end gap-1">
                                                {u.isLocked && (
                                                    <button
                                                        type="button"
                                                        onClick={() => setConfirmUnlock(u)}
                                                        className="p-1.5 text-emerald-600 hover:text-emerald-700 hover:bg-emerald-50 rounded-md transition-colors"
                                                        title="Mở khóa tài khoản"
                                                    >
                                                        <svg
                                                            className="w-4 h-4"
                                                            fill="none"
                                                            stroke="currentColor"
                                                            viewBox="0 0 24 24"
                                                        >
                                                            <path
                                                                strokeLinecap="round"
                                                                strokeLinejoin="round"
                                                                strokeWidth={1.8}
                                                                d="M8 11V7a4 4 0 118 0v4M5 11h14a2 2 0 012 2v7a2 2 0 01-2 2H5a2 2 0 01-2-2v-7a2 2 0 012-2z"
                                                            />
                                                        </svg>
                                                    </button>
                                                )}
                                                {!u.isLocked && u.role !== ROLE.ADMIN && (
                                                    <button
                                                        type="button"
                                                        onClick={() => setConfirmLock(u)}
                                                        className="p-1.5 text-amber-600 hover:text-amber-700 hover:bg-amber-50 rounded-md transition-colors"
                                                        title="Khóa tài khoản"
                                                    >
                                                        <svg
                                                            className="w-4 h-4"
                                                            fill="none"
                                                            stroke="currentColor"
                                                            viewBox="0 0 24 24"
                                                        >
                                                            <path
                                                                strokeLinecap="round"
                                                                strokeLinejoin="round"
                                                                strokeWidth={1.8}
                                                                d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z"
                                                            />
                                                        </svg>
                                                    </button>
                                                )}
                                                <button
                                                    type="button"
                                                    onClick={() => openEdit(u)}
                                                    className="p-1.5 text-gray-500 hover:text-gray-900 hover:bg-gray-100 rounded-md transition-colors"
                                                    title="Sửa"
                                                >
                                                    <svg
                                                        className="w-4 h-4"
                                                        fill="none"
                                                        stroke="currentColor"
                                                        viewBox="0 0 24 24"
                                                    >
                                                        <path
                                                            strokeLinecap="round"
                                                            strokeLinejoin="round"
                                                            strokeWidth={1.8}
                                                            d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z"
                                                        />
                                                    </svg>
                                                </button>
                                                <button
                                                    type="button"
                                                    onClick={() => setConfirmDelete(u)}
                                                    className="p-1.5 text-gray-500 hover:text-red-600 hover:bg-red-50 rounded-md transition-colors"
                                                    title="Xóa"
                                                >
                                                    <svg
                                                        className="w-4 h-4"
                                                        fill="none"
                                                        stroke="currentColor"
                                                        viewBox="0 0 24 24"
                                                    >
                                                        <path
                                                            strokeLinecap="round"
                                                            strokeLinejoin="round"
                                                            strokeWidth={1.8}
                                                            d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16"
                                                        />
                                                    </svg>
                                                </button>
                                            </div>
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>

                    {/* Pagination */}
                    {totalPages > 0 && (
                        <Pagination
                            page={page}
                            totalPages={totalPages}
                            totalItems={totalUsers}
                            pageSize={pageSize}
                            loading={loading}
                            itemLabel="user"
                            onPageChange={(p) => setPage(p)}
                            onPageSizeChange={(s) => {
                                setPageSize(s);
                                setPage(1);
                            }}
                        />
                    )}
                </div>
            </div>

            {/* Modals */}
            <UserFormModal
                open={formOpen}
                user={editingUser}
                onClose={() => setFormOpen(false)}
                onSaved={() => load()}
            />

            <ConfirmDialog
                open={!!confirmDelete}
                title="Xóa user?"
                message={
                    confirmDelete
                        ? `Bạn có chắc muốn xóa user "${confirmDelete.username || confirmDelete.email}"? Hành động này không thể hoàn tác.`
                        : ""
                }
                confirmText="Xóa"
                cancelText="Hủy"
                danger
                loading={deleting}
                onConfirm={handleDelete}
                onCancel={() => !deleting && setConfirmDelete(null)}
            />

            <ConfirmDialog
                open={!!confirmUnlock}
                title="Mở khóa tài khoản?"
                message={
                    confirmUnlock
                        ? `Mở khóa tài khoản "${confirmUnlock.username || confirmUnlock.email}" và xóa các cache Redis liên quan? User có thể đăng nhập lại ngay sau khi mở khóa.`
                        : ""
                }
                confirmText="Mở khóa"
                cancelText="Hủy"
                loading={unlocking}
                onConfirm={handleUnlock}
                onCancel={() => !unlocking && setConfirmUnlock(null)}
            />

            <ConfirmDialog
                open={!!confirmLock}
                title="Khóa tài khoản?"
                message={
                    confirmLock
                        ? `Khóa tài khoản "${confirmLock.username || confirmLock.email}"? Tất cả thiết bị đang đăng nhập sẽ bị văng ra và user không thể đăng nhập lại cho đến khi được mở khóa.`
                        : ""
                }
                confirmText="Khóa tài khoản"
                cancelText="Hủy"
                danger
                loading={locking}
                onConfirm={handleLock}
                onCancel={() => !locking && setConfirmLock(null)}
            />
        </div>
    );
}

export default UserManagementPage;
